import type { FC } from "react";
import { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Table,
  TextInput,
  Select,
  Label,
  Alert,
  Spinner,
  ModalHeader,
  ModalBody,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
} from "flowbite-react";
import { HiPencilAlt, HiTrash } from "react-icons/hi";
import {
  getCommissionOverridesByWorker,
  createCommissionOverride,
  updateCommissionOverride,
  deleteCommissionOverride,
} from "../../services/commission-overrides";
import type {
  CommissionOverride,
  CreateCommissionOverrideData,
  OverrideType,
} from "../../types/commission-override";
import { getContractTypes } from "../../services/contract-types";
import { getContractCompanies } from "../../services/contract-companies";
import { getContractCategories } from "../../services/contract-categories";

interface CommissionOverridesModalProps {
  show: boolean;
  onClose: () => void;
  workerId: number;
  workerName: string;
}

const CommissionOverridesModal: FC<CommissionOverridesModalProps> = ({
  show,
  onClose,
  workerId,
  workerName,
}) => {
  const [overrides, setOverrides] = useState<CommissionOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Reference data
  const [contractTypes, setContractTypes] = useState<{ id: number; type: string }[]>([]);
  const [companies, setCompanies] = useState<{ id: number; company: string }[]>([]);
  const [categories, setCategories] = useState<{ id: number; category: string }[]>([]);

  // Form state
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<CreateCommissionOverrideData>>({
    override_type: "percentage_adjustment",
    override_value: undefined,
    valid_from: new Date().toISOString().split("T")[0],
  });

  const [scope, setScope] = useState<"global" | "contract_type" | "company" | "category">("global");

  useEffect(() => {
    if (show && workerId) {
      fetchData();
    }
  }, [show, workerId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overridesData, typesData, companiesData, categoriesData] = await Promise.all([
        getCommissionOverridesByWorker(workerId),
        getContractTypes(),
        getContractCompanies(),
        getContractCategories(),
      ]);
      setOverrides(overridesData);
      setContractTypes(typesData);
      setCompanies(companiesData);
      setCategories(categoriesData);
    } catch (err) {
      setError("Failed to load data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      override_type: "percentage_adjustment",
      override_value: undefined,
      valid_from: new Date().toISOString().split("T")[0],
    });
    setScope("global");
    setIsEditing(false);
    setEditingId(null);
    setError(null);
    setSuccess(null);
  };

  const handleStartEdit = (override: CommissionOverride) => {
    setEditingId(override.id);
    setIsEditing(true);
    
    // Determine scope
    let currentScope: "global" | "contract_type" | "company" | "category" = "global";
    if (override.contract_type_id) currentScope = "contract_type";
    else if (override.company_name) currentScope = "company";
    else if (override.category_name) currentScope = "category";
    
    setScope(currentScope);
    setFormData({
      contract_type_id: override.contract_type_id,
      company_name: override.company_name,
      category_name: override.category_name,
      override_type: override.override_type,
      override_value: override.override_value,
      valid_from: override.valid_from,
      valid_to: override.valid_to,
    });
  };

  const validateForm = () => {
    if (formData.override_value === undefined || formData.override_value === null) return "Value is required";
    if (!formData.valid_from) return "Valid from date is required";
    
    if (scope === "contract_type" && !formData.contract_type_id) return "Contract Type is required";
    if (scope === "company" && !formData.company_name) return "Company is required";
    if (scope === "category" && !formData.category_name) return "Category is required";
    
    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: CreateCommissionOverrideData = {
        worker_id: workerId,
        override_type: formData.override_type as OverrideType,
        override_value: Number(formData.override_value),
        valid_from: formData.valid_from!,
        valid_to: formData.valid_to || null,
        contract_type_id: scope === "contract_type" ? Number(formData.contract_type_id) : undefined,
        company_name: scope === "company" ? formData.company_name : undefined,
        category_name: scope === "category" ? formData.category_name : undefined,
      };

      if (editingId) {
        await updateCommissionOverride(editingId, data);
        setSuccess("Override updated successfully");
      } else {
        await createCommissionOverride(data);
        setSuccess("Override created successfully");
      }

      await fetchData();
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save override");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this override?")) return;
    
    setLoading(true);
    try {
      await deleteCommissionOverride(id);
      setSuccess("Override deleted successfully");
      await fetchData();
    } catch (err) {
      setError("Failed to delete override");
    } finally {
      setLoading(false);
    }
  };

  const getScopeLabel = (override: CommissionOverride) => {
    if (override.contract_type_id) {
      const type = contractTypes.find(t => t.id === override.contract_type_id);
      return `Contract Type: ${type?.type || override.contract_type_id}`;
    }
    if (override.company_name) {
      return `Company: ${override.company_name}`;
    }
    if (override.category_name) {
      return `Category: ${override.category_name}`;
    }
    return "Global";
  };

  return (
    <Modal show={show} onClose={onClose} size="4xl">
      <ModalHeader>
        Commission Overrides - {workerName}
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="failure" className="mb-4">{error}</Alert>}
        {success && <Alert color="success" className="mb-4">{success}</Alert>}

        {/* Form Section */}
        <div className="mb-8 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="mb-4 text-lg font-medium">
            {isEditing ? "Edit Override" : "Add New Override"}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Scope</Label>
              <Select
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value as any);
                  setFormData(prev => ({
                    ...prev,
                    contract_type_id: undefined,
                    company_name: undefined,
                    category_name: undefined
                  }));
                }}
              >
                <option value="global">Global (All Contracts)</option>
                <option value="contract_type">Specific Contract Type</option>
                <option value="company">Specific Company</option>
                <option value="category">Specific Category</option>
              </Select>
            </div>

            {scope === "contract_type" && (
              <div>
                <Label>Contract Type</Label>
                <Select
                  value={formData.contract_type_id || ""}
                  onChange={(e) => setFormData({ ...formData, contract_type_id: Number(e.target.value) })}
                >
                  <option value="">Select Type</option>
                  {contractTypes.map(t => (
                    <option key={t.id} value={t.id}>{t.type}</option>
                  ))}
                </Select>
              </div>
            )}

            {scope === "company" && (
              <div>
                <Label>Company</Label>
                <Select
                  value={formData.company_name || ""}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                >
                  <option value="">Select Company</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.company}>{c.company}</option>
                  ))}
                </Select>
              </div>
            )}

            {scope === "category" && (
              <div>
                <Label>Category</Label>
                <Select
                  value={formData.category_name || ""}
                  onChange={(e) => setFormData({ ...formData, category_name: e.target.value })}
                >
                  <option value="">Select Category</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.category}>{c.category}</option>
                  ))}
                </Select>
              </div>
            )}

            <div>
              <Label>Type</Label>
              <Select
                value={formData.override_type}
                onChange={(e) => setFormData({ ...formData, override_type: e.target.value as OverrideType })}
              >
                <option value="percentage_adjustment">Percentage Adjustment (%)</option>
                <option value="fixed_value_adjustment">Fixed Value Adjustment (+/- €)</option>
                <option value="fixed_total_value">Fixed Total Value (€)</option>
              </Select>
            </div>

            <div>
              <Label>Value</Label>
              <TextInput
                type="number"
                step="0.01"
                value={formData.override_value ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData({ 
                    ...formData, 
                    override_value: val === "" ? undefined : Number(val) 
                  });
                }}
              />
            </div>

            <div>
              <Label>Valid From</Label>
              <TextInput
                type="date"
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
              />
            </div>

            <div>
              <Label>Valid To (Optional)</Label>
              <TextInput
                type="date"
                value={formData.valid_to || ""}
                onChange={(e) => setFormData({ ...formData, valid_to: e.target.value || undefined })}
              />
            </div>
          </div>
          
          <div className="mt-4 flex gap-2">
            <Button onClick={handleSave} disabled={loading}>
              {loading ? <Spinner size="sm" /> : (isEditing ? "Update" : "Add")}
            </Button>
            {isEditing && (
              <Button color="gray" onClick={resetForm}>
                Cancel
              </Button>
            )}
          </div>
        </div>

        {/* List Section */}
        <Table>
          <TableHead>
            <TableHeadCell>Scope</TableHeadCell>
            <TableHeadCell>Type</TableHeadCell>
            <TableHeadCell>Value</TableHeadCell>
            <TableHeadCell>Valid From</TableHeadCell>
            <TableHeadCell>Valid To</TableHeadCell>
            <TableHeadCell>Actions</TableHeadCell>
          </TableHead>
          <TableBody className="divide-y">
            {overrides.map((override) => (
              <TableRow key={override.id}>
                <TableCell>{getScopeLabel(override)}</TableCell>
                <TableCell className="capitalize">{override.override_type.replace(/_/g, " ")}</TableCell>
                <TableCell>{override.override_value}</TableCell>
                <TableCell>{new Date(override.valid_from).toLocaleDateString()}</TableCell>
                <TableCell>
                  {override.valid_to ? new Date(override.valid_to).toLocaleDateString() : "-"}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button size="xs" color="light" onClick={() => handleStartEdit(override)}>
                      <HiPencilAlt className="h-4 w-4" />
                    </Button>
                    <Button size="xs" color="failure" onClick={() => handleDelete(override.id)}>
                      <HiTrash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {overrides.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center">
                  No overrides found for this worker.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ModalBody>
    </Modal>
  );
};

export default CommissionOverridesModal;
